// Consent-approved landing handoff. Marketing source never controls auth,
// redirects, plan selection or the separate MCP signup entry signal.
export const ATTRIBUTION_PARAM = 'dathent_attribution';
export const FIRST_TOUCH_KEY = 'dathent.acquisition.firstTouch.v1';
export const ATTRIBUTION_TTL_MS = 7 * 24 * 60 * 60 * 1000;
const MAX_BYTES = 1500;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
let memoryFirstTouch = null;

function sessionStorageOrNull() {
  try { return globalThis.window?.sessionStorage ?? null; } catch { return null; }
}

function boundedText(value, max) {
  return typeof value === 'string' ? value.replace(/[\u0000-\u001f\u007f]/g, '').trim().slice(0, max) || null : null;
}

export function attributionReferrer(value) {
  try {
    const url = new URL(value);
    return /^https?:$/.test(url.protocol) && !url.username && !url.password ? url.origin : null;
  } catch { return null; }
}

export function attributionPath(value) {
  if (typeof value !== 'string' || !value.startsWith('/') || value.startsWith('//')) return null;
  return boundedText(value.split(/[?#]/, 1)[0], 256);
}

export function sanitizeAcquisitionAttribution(value = {}) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) value = {};
  return {
    referrer: attributionReferrer(value.referrer),
    utmSource: boundedText(value.utmSource, 100),
    utmMedium: boundedText(value.utmMedium, 100),
    utmCampaign: boundedText(value.utmCampaign, 100),
    landingPath: attributionPath(value.landingPath),
    uxSessionId: typeof value.uxSessionId === 'string' && UUID.test(value.uxSessionId) ? value.uxSessionId : null,
  };
}

export function parseAttributionHandoff(raw, now = Date.now()) {
  try {
    if (typeof raw !== 'string' || new TextEncoder().encode(raw).length > MAX_BYTES) return null;
    const value = JSON.parse(raw);
    if (!value || Array.isArray(value) || value.v !== 1 || value.consent !== true
      || !Number.isSafeInteger(value.capturedAt) || value.capturedAt < now - ATTRIBUTION_TTL_MS
      || value.capturedAt > now + 5 * 60 * 1000) return null;
    for (const [key, max] of [['utmSource', 100], ['utmMedium', 100], ['utmCampaign', 100], ['landingPath', 256]]) {
      if (value[key] != null && (typeof value[key] !== 'string' || value[key].length > max)) return null;
    }
    const fields = sanitizeAcquisitionAttribution(value);
    if ((value.referrer != null && !fields.referrer) || (value.landingPath != null && !fields.landingPath)
      || (value.uxSessionId != null && !fields.uxSessionId)) return null;
    return { v: 1, capturedAt: value.capturedAt, consent: true, ...fields };
  } catch { return null; }
}

export function readFirstTouchAttribution(storage = sessionStorageOrNull(), now = Date.now()) {
  try {
    const raw = storage?.getItem(FIRST_TOUCH_KEY);
    if (raw != null) {
      const record = parseAttributionHandoff(raw, now);
      if (!record) storage?.removeItem(FIRST_TOUCH_KEY);
      memoryFirstTouch = record;
      return record;
    }
  } catch { /* The in-memory handoff still works when storage is blocked. */ }
  memoryFirstTouch = parseAttributionHandoff(JSON.stringify(memoryFirstTouch), now);
  return memoryFirstTouch;
}

// Filter only our parameter from the original query, without reserializing
// OAuth state/next, plan, site or the fragment. Preserve history.state too.
export function captureAttributionHandoff(browser = globalThis.window, storage = sessionStorageOrNull(), now = Date.now()) {
  try {
    const { pathname, search, hash } = browser.location;
    const parts = (search || '').replace(/^\?/, '').split('&');
    const handoffs = [];
    const remaining = parts.filter((part) => {
      const separator = part.indexOf('=');
      const key = separator < 0 ? part : part.slice(0, separator);
      try {
        if (decodeURIComponent(key.replace(/\+/g, ' ')) !== ATTRIBUTION_PARAM) return true;
      } catch { return true; }
      try { handoffs.push(separator < 0 ? '' : decodeURIComponent(part.slice(separator + 1).replace(/\+/g, ' '))); }
      catch { handoffs.push(''); }
      return false;
    });
    if (!handoffs.length) return readFirstTouchAttribution(storage, now);
    try {
      browser.history.replaceState(browser.history.state, '', `${pathname}${remaining.join('&') ? `?${remaining.join('&')}` : ''}${hash || ''}`);
    } catch { /* URL cleanup must never block signup or OAuth. */ }
    const record = handoffs.length === 1 ? parseAttributionHandoff(handoffs[0], now) : null;
    const existing = readFirstTouchAttribution(storage, now);
    if (!record || (existing && existing.capturedAt <= record.capturedAt)) return existing;
    memoryFirstTouch = record;
    try { storage?.setItem(FIRST_TOUCH_KEY, JSON.stringify(record)); } catch { /* Best effort. */ }
    return record;
  } catch { return null; }
}

export function googleAuthHref(apiBase, next, attribution) {
  const base = `${apiBase}/api/oauth/google/start${next ? `?next=${encodeURIComponent(next)}` : ''}`;
  try {
    const fields = sanitizeAcquisitionAttribution(attribution);
    if (!Object.values(fields).some(Boolean)) return base;
    const raw = JSON.stringify(fields);
    if (new TextEncoder().encode(raw).length > MAX_BYTES) return base;
    return `${base}${next ? '&' : '?'}attribution=${encodeURIComponent(raw)}`;
  } catch { return base; }
}
